import { useEffect, useState } from 'react'
import AdminShell from './AdminShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'

/**
 * Hotel Integrations — super-admin surface listing every platform hotel supplier
 * (Expedia, RateHawk, Opera, …) with a master on/off toggle. Turning a provider
 * off removes it everywhere: customer search, the reseller curation tab, and
 * booking. Backed by /api/admin/hotel-integrations/providers.
 */
export default function HotelIntegrations() {
  const [providers, setProviders] = useState([])
  const [catalog, setCatalog] = useState([]) // suppliers we can add that have no row yet
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingId, setSavingId] = useState(null)
  const [credFor, setCredFor] = useState(null) // { providerType, label, ...schema, mode?, existing? } open in the modal
  const [testingId, setTestingId] = useState(null)
  const [testResult, setTestResult] = useState({}) // providerId -> last test-prebook result

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/admin/hotel-integrations/providers')
      .then(r => { setProviders(r.data.providers || []); setCatalog(r.data.catalog || []) })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  async function toggleActive(p, next) {
    setSavingId(p.id)
    // Optimistic — reflect both active + customer-search (they move together).
    setProviders(prev => prev.map(x => x.id === p.id ? { ...x, isActive: next, searchEnabled: next } : x))
    try {
      await tournamentApi.patch(`/admin/hotel-integrations/providers/${p.id}/active`, { isActive: next })
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
      reload() // revert to server truth
    } finally { setSavingId(null) }
  }

  // Zero-cost live credential test: search + prebook (locks a price, never charges).
  async function testKey(p) {
    setTestingId(p.id)
    setTestResult(r => ({ ...r, [p.id]: null }))
    try {
      const { data } = await tournamentApi.post(`/admin/hotel-integrations/providers/${p.id}/test-prebook`)
      setTestResult(r => ({ ...r, [p.id]: data }))
    } catch (e) {
      setTestResult(r => ({ ...r, [p.id]: { ok: false, keyAccepted: false, error: e?.response?.data?.message || e.message } }))
    } finally { setTestingId(null) }
  }

  const activeCount = providers.filter(p => p.isActive).length

  return (
    <AdminShell title="Hotel Integrations">
      <p style={{ color: '#64748b', margin: '0 0 20px', maxWidth: 760, lineHeight: 1.5 }}>
        Every hotel supplier connected to InteGolf. Toggle a provider <b>off</b> to remove it
        everywhere — customer hotel search, the reseller curation tab, and booking — without
        deleting its credentials. Toggle it back on to go live again. Turning one provider off
        does not affect the others (e.g. you can run RateHawk while Expedia is off).
      </p>

      <CacheHealth />

      <CoverageProbe />

      <ExpediaCapturePanel />

      {loading ? (
        <div style={empty}>Loading providers…</div>
      ) : error ? (
        <div style={{ ...empty, color: '#b91c1c', borderColor: '#fecaca', background: '#fef2f2' }}>{error}</div>
      ) : providers.length === 0 ? (
        <div style={empty}>No hotel providers configured yet.</div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
            {activeCount} of {providers.length} active
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760 }}>
            {providers.map(p => (
              <div key={p.id}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                padding: '16px 18px', border: '1px solid #e2e8f0', borderRadius: 12,
                background: p.isActive ? '#fff' : '#f8fafc',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 16, color: '#0f172a' }}>{p.label}</strong>
                    <code style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{p.providerType}</code>
                    {p.mode && <Badge color={p.mode === 'production' || p.mode === 'live' ? 'green' : 'amber'}>{p.mode}</Badge>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    <Badge color={p.hasCredentials ? 'slate' : 'red'}>
                      {p.hasCredentials ? 'Credentials set' : 'No credentials'}
                    </Badge>
                    <Badge color={p.isActive && p.searchEnabled ? 'green' : 'slate'}>
                      {p.isActive && p.searchEnabled ? 'In customer search' : 'Hidden from customers'}
                    </Badge>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {p.hasCredentials && (
                    <button onClick={() => testKey(p)} disabled={testingId === p.id} style={btnSm}>
                      {testingId === p.id ? 'Testing…' : 'Test key'}
                    </button>
                  )}
                  {p.schema && (
                    <button onClick={() => setCredFor({ providerType: p.providerType, label: p.label, ...p.schema, mode: p.mode && p.schema.modes.includes(p.mode) ? p.mode : p.schema.modes[0], existing: p.hasCredentials })}
                      style={btnSm}>{p.hasCredentials ? 'Edit key' : 'Add key'}</button>
                  )}
                  <span style={{ fontSize: 13, fontWeight: 600, color: p.isActive ? '#0f766e' : '#94a3b8' }}>
                    {p.isActive ? 'Active' : 'Off'}
                  </span>
                  <Toggle
                    checked={p.isActive}
                    disabled={savingId === p.id}
                    onChange={(next) => toggleActive(p, next)}
                  />
                </div>
              </div>
              {testResult[p.id] && <TestResult r={testResult[p.id]} />}
              </div>
            ))}
          </div>

          {catalog.length > 0 && (
            <div style={{ marginTop: 16, maxWidth: 760 }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>Add a supplier</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {catalog.map(c => (
                  <button key={c.providerType} onClick={() => setCredFor({ ...c, mode: c.modes[0], existing: false })}
                    style={{ ...btnSm, borderStyle: 'dashed' }}>+ {c.label}</button>
                ))}
              </div>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '8px 0 0', lineHeight: 1.5 }}>
                Adds the supplier with your API key and leaves it <b>Off</b> — flip the toggle to go live once verified.
              </p>
            </div>
          )}

          {credFor && <CredentialsModal spec={credFor} onClose={() => setCredFor(null)} onSaved={() => { setCredFor(null); reload() }} />}
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '16px 0 0', maxWidth: 760, lineHeight: 1.5 }}>
            Changes take effect on the next hotel search (search results are briefly cached, so
            allow up to ~30 minutes for a fully warm cache to refresh, or use a grouping's
            <b> Refresh</b> button on the Hotels tab).
          </p>
        </>
      )}
    </AdminShell>
  )
}

const empty = {
  padding: '40px 20px', textAlign: 'center', color: '#94a3b8',
  border: '1px dashed #e2e8f0', borderRadius: 12, background: '#fafafa', fontSize: 14,
}

// Add / edit a hotel supplier's API credentials + mode. Keys are write-only —
// never returned by the API — so on edit, a blank field keeps the stored value.
// New suppliers are created OFF; the admin flips the master toggle to go live.
function CredentialsModal({ spec, onClose, onSaved }) {
  const [mode, setMode] = useState(spec.mode || spec.modes[0])
  const [fields, setFields] = useState({})
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function save() {
    setSaving(true); setErr(null)
    try {
      await tournamentApi.put('/admin/hotel-integrations/providers/credentials', { providerType: spec.providerType, mode, fields })
      onSaved()
    } catch (e) { setErr(e?.response?.data?.message || e.message); setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(460px, 100%)', padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{spec.existing ? 'Edit' : 'Add'} {spec.label}</div>
        <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
          Keys are stored securely and never shown again.{spec.existing ? ' Leave a field blank to keep the current value.' : ''}
        </div>
        <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
          <label style={lbl}>Mode
            <select value={mode} onChange={e => setMode(e.target.value)} style={inp}>
              {spec.modes.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          {spec.fields.map(f => (
            <label key={f.key} style={lbl}>{f.label}{spec.modeScoped ? ` (${mode})` : ''}
              <input type={f.secret ? 'password' : 'text'} autoComplete="off" value={fields[f.key] || ''}
                placeholder={f.placeholder || (spec.existing ? 'leave blank to keep current' : '')}
                onChange={e => setFields(s => ({ ...s, [f.key]: e.target.value }))} style={inp} />
            </label>
          ))}
        </div>
        {err && <div style={{ marginTop: 10, fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btnSm}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...btnSm, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}
const lbl = { display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600, color: '#475569' }
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 400, boxSizing: 'border-box', width: '100%' }

// Verdict from a zero-cost test-prebook: key rejected (red) / key + inventory
// verified (green, with a "price locked, no charge" note when prebook succeeded)
// / key OK but no sample inventory (amber — e.g. an empty sandbox).
function TestResult({ r }) {
  const money = (v, c) => v == null ? null : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c || ''}`.trim()
  let tone, title, detail
  if (!r.keyAccepted) {
    tone = 'red'; title = `Key rejected${r.mode ? ` (${r.mode})` : ''}`
    detail = r.error || 'The supplier did not accept the key.'
  } else if (r.sample) {
    tone = 'green'; title = `Verified — key + inventory OK${r.mode ? ` (${r.mode})` : ''}`
    const priceLock = r.ok
      ? `price-lock confirmed${r.prebook?.price != null ? ` at ${money(r.prebook.price, r.prebook.currency)}` : ''} · no charge`
      : `price-lock step not confirmed (${r.error || r.prebook?.status || 'n/a for this supplier'})`
    detail = `Found ${r.sample.hotel}${r.sample.price != null ? ` at ${money(r.sample.price, r.sample.currency)}` : ''} · ${r.hotelsFound} priced · ${priceLock}.`
  } else {
    tone = 'amber'; title = `Key authenticated${r.mode ? ` (${r.mode})` : ''} — no sample inventory`
    detail = r.message || 'No priced hotels for the sample area/dates (the sandbox may be empty, or try different coords).'
  }
  const C = { green: { bg: '#f0fdf4', bd: '#a7f3d0', fg: '#166534' }, amber: { bg: '#fffbeb', bd: '#fde68a', fg: '#b45309' }, red: { bg: '#fef2f2', bd: '#fecaca', fg: '#b91c1c' } }[tone]
  return (
    <div style={{ marginTop: 6, padding: '8px 12px', border: `1px solid ${C.bd}`, background: C.bg, borderRadius: 8, fontSize: 12.5, lineHeight: 1.45 }}>
      <span style={{ fontWeight: 700, color: C.fg }}>{tone === 'green' ? '✓ ' : tone === 'amber' ? '! ' : '✗ '}{title}</span>
      <span style={{ color: '#475569' }}> — {detail}</span>
    </div>
  )
}

/**
 * Hotel cache health — the flapping watch. Surfaces the RateHawk 429 rate (should
 * sit near zero now the hot path is search-by-ID at 150/min), the shared search
 * cache's freshness + empty cells, and how many regions are on the fast by-ID
 * path. Read-only; backed by /api/admin/hotel-integrations/cache-health.
 */
// Expedia certification capture — record one real production booking's raw Rapid
// chain (shop -> price_check -> book -> retrieve -> cancel) made through the normal
// booking UI, then download it in Expedia's approved log format (card redacted).
function ExpediaCapturePanel() {
  const [sessions, setSessions] = useState([])
  const [active, setActive] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState(false)

  function load() {
    setLoading(true)
    tournamentApi.get('/admin/hotel-integrations/expedia-capture/sessions')
      .then(r => { setSessions(r.data.sessions || []); setActive(r.data.activeSession || null) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(() => { if (open) load() }, [open])

  async function start() {
    setBusy(true); setErr(null)
    try { await tournamentApi.post('/admin/hotel-integrations/expedia-capture/start', {}); load() }
    catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function stop() {
    setBusy(true); setErr(null)
    try { await tournamentApi.post('/admin/hotel-integrations/expedia-capture/stop', {}); load() }
    catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function del(session) {
    if (!window.confirm('Delete this capture session and its logs?')) return
    setBusy(true); setErr(null)
    try { await tournamentApi.delete(`/admin/hotel-integrations/expedia-capture/sessions/${session}`); load() }
    catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function download(session, format) {
    try {
      const url = `/admin/hotel-integrations/expedia-capture/sessions/${session}/download${format === 'json' ? '?format=json' : ''}`
      const r = await tournamentApi.get(url, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = format === 'json' ? `expedia-capture-${session}.json` : `expedia-production-booking-log-${session}.txt`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(blobUrl)
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 0 20px', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Expedia certification capture {active ? '🔴' : ''}</span>
        <span style={{ fontSize: 12, color: '#64748b' }}>{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.55, margin: '0 0 12px' }}>
            Records the raw Expedia Rapid request/response chain for one <b>real production</b> booking
            so you can send Expedia a "full log of a real production booking" in their approved format.
            The card number and CVC are redacted automatically. <b>Steps:</b> (1) Start capture,
            (2) make ONE production booking — single room, <b>fully refundable</b> — through the normal
            booking flow, taking your screenshots, (3) <b>cancel</b> that booking, (4) Stop, then Download.
          </p>

          {err && <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 10 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {active ? (
              <>
                <Badge color="red">Recording — session {String(active).slice(0, 12)}…</Badge>
                <button onClick={stop} disabled={busy} style={btnSm}>{busy ? 'Working…' : 'Stop capture'}</button>
              </>
            ) : (
              <button onClick={start} disabled={busy} style={{ ...btnSm, borderColor: '#0f766e', color: '#0f766e', fontWeight: 700 }}>
                {busy ? 'Working…' : 'Start capture'}
              </button>
            )}
            <button onClick={load} disabled={loading} style={btnSm}>{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {sessions.length === 0 ? (
            <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No capture sessions yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sessions.map(s => (
                <div key={s.Session} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', background: s.Status === 'active' ? '#fef2f2' : '#f8fafc' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {s.Label || 'Capture'} <Badge color={s.Status === 'active' ? 'red' : 'slate'}>{s.Status}</Badge>
                      </div>
                      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                        {new Date(s.StartedAt).toLocaleString()} · {s.Calls || 0} calls
                        {s.ItineraryId ? <> · itinerary <code style={{ color: '#0f766e' }}>{s.ItineraryId}</code></> : null}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                      <button onClick={() => download(s.Session, 'txt')} disabled={!s.Calls} style={btnSm}>Download log</button>
                      <button onClick={() => download(s.Session, 'json')} disabled={!s.Calls} style={btnSm}>JSON</button>
                      <button onClick={() => del(s.Session)} style={{ ...btnSm, color: '#b91c1c', borderColor: '#fecaca' }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CacheHealth() {
  const [h, setH] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [showDetail, setShowDetail] = useState(false)
  function load() {
    setLoading(true); setErr(null)
    tournamentApi.get('/admin/hotel-integrations/cache-health')
      .then(r => setH(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  if (loading) return <div style={panel}>Loading cache health…</div>
  if (err) return <div style={{ ...panel, color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }}>{err}</div>
  if (!h) return null

  const rh = h.ratehawk || {}
  const sc = h.searchCache || {}
  const ri = h.regionIds || {}
  const attempts = (rh.calls || 0) + (rh.timeouts || 0)
  const timeoutRate = attempts ? Math.round(((rh.timeouts || 0) / attempts) * 100) : 0
  const flap = (rh.http429 || 0) > 0 || (sc.emptyCells || 0) > 0 || timeoutRate >= 10
  const modes = (h.providers || []).filter(p => p.searchMode).map(p => `${p.label} ${p.searchMode}`).join(', ')
  const byEp = rh.byEndpoint || {}
  const epRows = Object.entries(byEp).sort((a, b) => (b[1].timeouts + b[1].http429) - (a[1].timeouts + a[1].http429))

  return (
    <div style={{ ...panel, borderColor: flap ? '#fde68a' : '#a7f3d0', background: flap ? '#fffbeb' : '#f0fdf4' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <strong style={{ fontSize: 14, color: '#0f172a' }}>
          Hotel cache health <span style={{ fontWeight: 400, color: '#94a3b8' }}>· flapping watch</span>
        </strong>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowDetail(true)} style={btnSm}>Details</button>
          <button onClick={load} style={btnSm}>Refresh</button>
        </div>
      </div>
      {showDetail && <HotelCacheModal onClose={() => setShowDetail(false)} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Stat label="RateHawk 429s" value={rh.http429 ?? '—'} bad={(rh.http429 || 0) > 0}
          sub={`${rh.calls ?? 0} calls · ${rh.errors ?? 0} err`} note={`since restart ${rh.sinceMinutes ?? 0}m`} />
        <Stat label="Timeouts" value={`${rh.timeouts ?? 0}${attempts ? ` (${timeoutRate}%)` : ''}`} bad={timeoutRate >= 10}
          sub={`avg ${rh.avgMs != null ? rh.avgMs + 'ms' : '—'} · max ${rh.maxMs != null ? rh.maxMs + 'ms' : '—'}`} />
        <Stat label="Search cache" value={`${sc.fresh ?? 0}/${sc.cells ?? 0} fresh`}
          sub={`${sc.emptyCells ?? 0} empty · avg ${sc.avgHotelCount ?? 0} hotels`} badSub={(sc.emptyCells || 0) > 0} />
        <Stat label="By-ID regions" value={`${ri.freshRegions ?? 0}/${ri.regions ?? 0}`}
          sub={`${ri.totalIds ?? 0} ids · avg ${ri.avgIdsPerRegion ?? 0}/region`} />
      </div>

      {epRows.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Per-endpoint (since restart)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: '#94a3b8', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', fontWeight: 600, padding: '3px 6px' }}>endpoint</th>
                <th style={{ fontWeight: 600, padding: '3px 6px' }}>calls</th>
                <th style={{ fontWeight: 600, padding: '3px 6px' }}>429</th>
                <th style={{ fontWeight: 600, padding: '3px 6px' }}>err</th>
                <th style={{ fontWeight: 600, padding: '3px 6px' }}>timeout</th>
                <th style={{ fontWeight: 600, padding: '3px 6px' }}>avg</th>
                <th style={{ fontWeight: 600, padding: '3px 6px' }}>max</th>
              </tr>
            </thead>
            <tbody>
              {epRows.map(([ep, e]) => {
                const hot = (e.timeouts || 0) > 0 || (e.http429 || 0) > 0
                return (
                  <tr key={ep} style={{ borderTop: '1px solid #eef2f7', textAlign: 'right' }}>
                    <td style={{ textAlign: 'left', padding: '3px 6px', fontFamily: 'monospace', color: '#334155' }}>{ep}</td>
                    <td style={{ padding: '3px 6px', color: '#334155' }}>{e.calls ?? 0}</td>
                    <td style={{ padding: '3px 6px', color: (e.http429 || 0) > 0 ? '#b91c1c' : '#94a3b8' }}>{e.http429 ?? 0}</td>
                    <td style={{ padding: '3px 6px', color: (e.errors || 0) > 0 ? '#b45309' : '#94a3b8' }}>{e.errors ?? 0}</td>
                    <td style={{ padding: '3px 6px', color: (e.timeouts || 0) > 0 ? '#b91c1c' : '#94a3b8', fontWeight: hot ? 700 : 400 }}>{e.timeouts ?? 0}</td>
                    <td style={{ padding: '3px 6px', color: '#334155' }}>{e.avgMs != null ? e.avgMs + 'ms' : '—'}</td>
                    <td style={{ padding: '3px 6px', color: (e.maxMs || 0) >= 15000 ? '#b45309' : '#334155' }}>{e.maxMs != null ? e.maxMs + 'ms' : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 }}>
        429s + timeouts should stay near zero on the by-ID path. The per-endpoint table shows where
        time goes — <code>serp_hotels</code> (by-id) and <code>hotel_info</code> (content) are the ones to watch.
        Provider mode: {modes || '—'}. As of {h.generatedAt ? new Date(h.generatedAt).toLocaleTimeString() : '—'} · provider stats reset on each deploy.
      </div>
    </div>
  )
}

function Stat({ label, value, sub, note, bad, badSub }) {
  return (
    <div style={{ padding: '12px 14px', border: `1px solid ${bad || badSub ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 10, background: '#fff' }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: bad ? '#b91c1c' : '#0f172a', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: badSub ? '#b91c1c' : '#94a3b8', marginTop: 2 }}>{sub}</div>}
      {note && <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 2 }}>{note}</div>}
    </div>
  )
}

const panel = { padding: '16px 18px', border: '1px solid #e2e8f0', borderRadius: 12, maxWidth: 760, marginBottom: 20 }
const btnSm = { fontSize: 12, padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', cursor: 'pointer', color: '#334155' }

const fmtTs = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) } catch { return '' } }
const fmtAge = (h) => h == null ? '—' : (h < 24 ? `${h}h` : `${Math.round(h / 24)}d`)
const EVENT_COLOR = { timeout: '#b91c1c', neterr: '#b91c1c', '429': '#b45309', slow: '#b45309' }

/**
 * Hotel cache detail — the deep-dive modal. Shows the transient story (recent
 * searches + events), the per-region id cache with age, search-cache freshness by
 * age, and content counts. Download JSON bundles health + detail + the deep
 * diagnostics export for offline processing. Read-only; secrets never leave the
 * server. Backed by /cache-detail + /cache-diagnostics.
 */
function HotelCacheModal({ onClose }) {
  const [health, setHealth] = useState(null)
  const [detail, setDetail] = useState(null)
  const [err, setErr] = useState('')
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    tournamentApi.get('/admin/hotel-integrations/cache-health').then(r => setHealth(r.data)).catch(e => setErr(e?.response?.data?.message || e.message))
    tournamentApi.get('/admin/hotel-integrations/cache-detail').then(r => setDetail(r.data)).catch(e => setErr(e?.response?.data?.message || e.message))
  }, [])

  async function download() {
    setDownloading(true)
    let diagnostics = null
    try { const { data } = await tournamentApi.get('/admin/hotel-integrations/cache-diagnostics'); diagnostics = data }
    catch (e) { diagnostics = { error: e?.response?.data?.message || e.message } }
    const payload = { generatedAt: new Date().toISOString(), health, detail, diagnostics }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `hotel-cache-diagnostics-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    setDownloading(false)
  }

  const rh = health?.ratehawk || {}
  const byEp = rh.byEndpoint || {}
  const epRows = Object.entries(byEp).sort((a, b) => (b[1].timeouts + b[1].http429) - (a[1].timeouts + a[1].http429))
  const b = detail?.searchCache?.buckets
  const regions = detail?.regions || []
  const searches = detail?.recent?.searches || []
  const events = detail?.recent?.events || []
  const demand = detail?.demand
  const lw = demand?.leadWindow
  const th = { padding: '4px 8px', fontWeight: 600, color: '#64748b', textAlign: 'left', fontSize: 12 }
  const td = { padding: '3px 8px', color: '#334155', fontSize: 12, borderTop: '1px solid #eef2f7' }
  const tdr = { ...td, textAlign: 'right' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 50, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 900, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Hotel cache diagnostics</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={download} disabled={downloading} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: downloading ? 'default' : 'pointer', opacity: downloading ? 0.6 : 1 }}>{downloading ? 'Preparing…' : 'Download JSON'}</button>
            <button onClick={onClose} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {/* Demand — the real-search signal that DRIVES warming */}
        {demand && (
          <Section title="Demand (drives warming)">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
              <MiniStat label="Searches (30d)" value={demand.searches ?? 0} sub={`${demand.cells ?? 0} cells`} />
              <MiniStat label="Hit rate" value={demand.hitRate != null ? demand.hitRate + '%' : '—'}
                sub={`${demand.hits ?? 0} hit · ${demand.misses ?? 0} miss · ${demand.stale ?? 0} stale`} />
              <MiniStat label="Empty / error" value={`${demand.empty ?? 0} / ${demand.errors ?? 0}`}
                sub={demand.lastSearch ? `last ${fmtTs(demand.lastSearch).split(', ')[1] || ''}` : 'no searches yet'}
                bad={(demand.errors || 0) > 0} />
              <MiniStat label="Lead window" value={lw ? `${lw.min}–${lw.max}d` : '—'}
                sub={lw ? (lw.dataDriven ? `data-driven · median ${lw.median}d · n=${lw.samples}` : `default · n=${lw.samples || 0}`) : ''}
                bad={lw && !lw.dataDriven} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: demand.topAreas?.length ? 8 : 0, lineHeight: 1.5 }}>
              The warm cron pre-warms every package area across the <b>lead window</b> (p10–p90 of real
              search-to-check-in days) and keeps the exact cells customers search fresh. Once real
              searches accumulate the window becomes <b>data-driven</b>; until then it uses a safe default.
            </div>
            {demand.topAreas?.length > 0 && (
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>most-searched area</th><th style={{ ...th, textAlign: 'right' }}>radius</th><th style={{ ...th, textAlign: 'right' }}>searches</th><th style={{ ...th, textAlign: 'right' }}>dates</th></tr></thead>
                  <tbody>{demand.topAreas.map((a, i) => (
                    <tr key={i}><td style={{ ...td, fontFamily: 'monospace' }}>{a.lat},{a.lng}</td>
                      <td style={tdr}>{a.radiusKm}km</td><td style={{ ...tdr, fontWeight: 600 }}>{a.searches}</td>
                      <td style={tdr}>{a.dates}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        {/* Per-endpoint latency */}
        {epRows.length > 0 && (
          <Section title="RateHawk per-endpoint (since restart)">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr><th style={th}>endpoint</th><th style={{ ...th, textAlign: 'right' }}>calls</th><th style={{ ...th, textAlign: 'right' }}>429</th><th style={{ ...th, textAlign: 'right' }}>err</th><th style={{ ...th, textAlign: 'right' }}>timeout</th><th style={{ ...th, textAlign: 'right' }}>avg</th><th style={{ ...th, textAlign: 'right' }}>max</th></tr></thead>
              <tbody>{epRows.map(([ep, e]) => (
                <tr key={ep}><td style={{ ...td, fontFamily: 'monospace' }}>{ep}</td><td style={tdr}>{e.calls}</td>
                  <td style={{ ...tdr, color: e.http429 ? '#b91c1c' : '#94a3b8' }}>{e.http429}</td>
                  <td style={{ ...tdr, color: e.errors ? '#b45309' : '#94a3b8' }}>{e.errors}</td>
                  <td style={{ ...tdr, color: e.timeouts ? '#b91c1c' : '#94a3b8' }}>{e.timeouts}</td>
                  <td style={tdr}>{e.avgMs != null ? e.avgMs + 'ms' : '—'}</td>
                  <td style={{ ...tdr, color: (e.maxMs || 0) >= 15000 ? '#b45309' : '#334155' }}>{e.maxMs != null ? e.maxMs + 'ms' : '—'}</td></tr>
              ))}</tbody>
            </table>
          </Section>
        )}

        {/* Search-cache freshness by age */}
        {b && (
          <Section title="Search cache — freshness by age">
            <div style={{ fontSize: 12.5, color: '#475569' }}>
              <b>{b.cells}</b> cells · <b style={{ color: '#166534' }}>{b.fresh} fresh</b> · {b.stale1h} stale &lt;1h · {b.stale24h} stale &lt;24h ·
              <b style={{ color: b.staleOld ? '#b45309' : '#94a3b8' }}> {b.staleOld} stale &gt;24h</b> (pruned nightly) ·
              <b style={{ color: b.emptyCells ? '#b91c1c' : '#94a3b8' }}> {b.emptyCells} empty</b>
            </div>
          </Section>
        )}

        {/* Recent searches — the transient timing story */}
        {searches.length > 0 && (
          <Section title={`Recent live searches (${searches.length})`}>
            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>time</th><th style={th}>mode</th><th style={th}>region</th><th style={{ ...th, textAlign: 'right' }}>ids→priced</th><th style={{ ...th, textAlign: 'right' }}>avail</th><th style={{ ...th, textAlign: 'right' }}>content</th><th style={{ ...th, textAlign: 'right' }}>total</th></tr></thead>
                <tbody>{searches.map((s, i) => (
                  <tr key={i}><td style={td}>{fmtTs(s.at).split(', ')[1] || fmtTs(s.at)}</td>
                    <td style={{ ...td, color: s.mode === 'geo' ? '#b45309' : '#166534', fontWeight: 600 }}>{s.mode}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{s.lat},{s.lng} r{s.radiusKm}</td>
                    <td style={tdr}>{s.ids}→{s.priced}</td>
                    <td style={tdr}>{s.availMs != null ? s.availMs + 'ms' : '—'}</td>
                    <td style={tdr}>{s.contentMs != null ? s.contentMs + 'ms' : '—'}</td>
                    <td style={{ ...tdr, fontWeight: 700, color: (s.totalMs || 0) >= 10000 ? '#b91c1c' : '#0f172a' }}>{s.totalMs != null ? s.totalMs + 'ms' : '—'}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Recent events — timeouts / 429s / slow / neterr */}
        {events.length > 0 && (
          <Section title={`Recent events (${events.length})`}>
            <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace' }}>
              {events.map((e, i) => (
                <div key={i} style={{ padding: '1px 0', color: '#475569' }}>
                  <span style={{ color: '#94a3b8' }}>{fmtTs(e.at).split(', ')[1] || ''}</span>{' '}
                  <b style={{ color: EVENT_COLOR[e.kind] || '#334155' }}>{e.kind}</b>{' '}
                  {e.endpoint}{e.ms != null ? ` ${e.ms}ms` : ''}{e.status ? ` (${e.status})` : ''}{e.code ? ` ${e.code}` : ''}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Region id cache — oldest first (next to refresh) */}
        {regions.length > 0 && (
          <Section title={`By-ID region cache (${regions.length}) — oldest first`}>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>region</th><th style={{ ...th, textAlign: 'right' }}>ids</th><th style={{ ...th, textAlign: 'right' }}>age</th><th style={{ ...th, textAlign: 'right' }}>state</th><th style={th}>last discovered</th></tr></thead>
                <tbody>{regions.map(r => (
                  <tr key={r.key}><td style={{ ...td, fontFamily: 'monospace' }}>{r.key}</td><td style={tdr}>{r.ids}</td>
                    <td style={{ ...tdr, color: (r.ageHours || 0) >= 168 ? '#b45309' : '#334155' }}>{fmtAge(r.ageHours)}</td>
                    <td style={{ ...tdr, color: r.fresh ? '#166534' : '#b45309' }}>{r.fresh ? 'fresh' : 'stale'}</td>
                    <td style={td}>{fmtTs(r.lastDiscovered)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </Section>
        )}

        {detail?.content && (
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
            Content cache: RateHawk {detail.content.ratehawk?.rows ?? 0} hotels ({detail.content.ratehawk?.fresh ?? 0} fresh) · Booking.com {detail.content.bookingcom?.rows ?? 0}.
            {' '}Download JSON for the full per-region + per-cell export.
          </div>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  )
}

function MiniStat({ label, value, sub, bad }) {
  return (
    <div style={{ padding: '8px 10px', border: `1px solid ${bad ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 8, background: '#fff' }}>
      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: bad ? '#b45309' : '#0f172a', marginTop: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    </div>
  )
}

function Badge({ children, color = 'slate' }) {
  const C = {
    green: { bg: '#ecfdf5', bd: '#a7f3d0', fg: '#047857' },
    amber: { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' },
    red:   { bg: '#fef2f2', bd: '#fecaca', fg: '#b91c1c' },
    slate: { bg: '#f1f5f9', bd: '#e2e8f0', fg: '#475569' },
  }[color] || {}
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
      background: C.bg, border: `1px solid ${C.bd}`, color: C.fg, whiteSpace: 'nowrap',
    }}>{children}</span>
  )
}

// Live per-provider coverage probe. Enter "lat, lng" (+ optional dates) and it
// runs each active supplier's search BYPASSING the cache, so you can see raw
// coverage for a spot (e.g. "does RateHawk return anything for Miami?").
function CoverageProbe() {
  const [coords, setCoords] = useState('')
  const [checkin, setCheckin] = useState('')
  const [checkout, setCheckout] = useState('')
  const [radiusKm, setRadiusKm] = useState('25')
  const [busy, setBusy] = useState(false)
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [rebuilding, setRebuilding] = useState(false)
  const [rebuild, setRebuild] = useState(null)

  function parseCoords() {
    return String(coords).trim().match(/^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)$/)
  }

  async function run() {
    const m = parseCoords()
    if (!m) { setErr('Enter coordinates as "lat, lng" (e.g. 25.7617, -80.1918)'); return }
    setBusy(true); setErr(null); setData(null)
    try {
      const params = { lat: m[1], lng: m[2], radiusKm: radiusKm || 25 }
      if (checkin) params.checkin = checkin
      if (checkout) params.checkout = checkout
      const r = await tournamentApi.get('/admin/hotel-integrations/coverage-probe', { params, timeout: 180000 })
      setData(r.data)
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }

  // Force a multi-date RateHawk re-discovery to rebuild a thin region's hotel-id
  // set (the fix for areas like Belleville where in-town hotels went missing).
  async function runRebuild() {
    const m = parseCoords()
    if (!m) { setErr('Enter coordinates as "lat, lng" first.'); return }
    setRebuilding(true); setErr(null); setRebuild(null)
    try {
      const r = await tournamentApi.post('/admin/hotel-integrations/rebuild-coverage',
        { lat: Number(m[1]), lng: Number(m[2]), radiusKm: Number(radiusKm) || 50 }, { timeout: 180000 })
      setRebuild(r.data)
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setRebuilding(false) }
  }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 20, maxWidth: 760, background: '#fff' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Coverage probe</div>
      <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
        Runs every active supplier's live search for a spot, bypassing the cache. Shows raw hotels found + how many are priced,
        and how long each took. A search that takes longer than ~15s gets dropped in the real customer flow (so it shows empty).
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: '#475569' }}>
          <div style={{ marginBottom: 2 }}>Coordinates (lat, lng)</div>
          <input value={coords} onChange={e => setCoords(e.target.value)} placeholder="25.7617, -80.1918"
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, width: 200 }} />
        </label>
        <label style={{ fontSize: 12, color: '#475569' }}>
          <div style={{ marginBottom: 2 }}>Radius km</div>
          <input value={radiusKm} onChange={e => setRadiusKm(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, width: 70 }} />
        </label>
        <label style={{ fontSize: 12, color: '#475569' }}>
          <div style={{ marginBottom: 2 }}>Check-in</div>
          <input type="date" value={checkin} onChange={e => setCheckin(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
        </label>
        <label style={{ fontSize: 12, color: '#475569' }}>
          <div style={{ marginBottom: 2 }}>Check-out</div>
          <input type="date" value={checkout} onChange={e => setCheckout(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
        </label>
        <button onClick={run} disabled={busy || rebuilding}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 600, cursor: (busy || rebuilding) ? 'default' : 'pointer', opacity: (busy || rebuilding) ? 0.6 : 1 }}>
          {busy ? 'Probing… (up to ~2 min)' : 'Run probe'}
        </button>
        <button onClick={runRebuild} disabled={busy || rebuilding} title="Force a multi-date RateHawk re-discovery to rebuild this area's hotel-id set (fixes missing in-town inventory)."
          style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #0f172a', background: '#fff', color: '#0f172a', fontSize: 13, fontWeight: 600, cursor: (busy || rebuilding) ? 'default' : 'pointer', opacity: (busy || rebuilding) ? 0.6 : 1 }}>
          {rebuilding ? 'Rebuilding…' : 'Rebuild coverage'}
        </button>
      </div>

      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>{err}</div>}

      {rebuild && (
        <div style={{ fontSize: 12.5, color: '#0f172a', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            RateHawk coverage rebuilt · {rebuild.before} → {rebuild.after} hotel ids
            {rebuild.priced != null && <span style={{ fontWeight: 400, color: '#475569' }}> · {rebuild.priced} priced on a test date</span>}
          </div>
          <div style={{ fontSize: 11.5, color: '#64748b' }}>
            discovered across {(rebuild.perDate || []).map(d => `${d.checkin}:${d.count ?? 'err'}`).join('  ')}
          </div>
          {rebuild.sample && rebuild.sample.length > 0 && (
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>e.g. {rebuild.sample.join(' · ')}</div>
          )}
        </div>
      )}

      {data && (
        <div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 8 }}>
            {data.query.checkin} → {data.query.checkout} · r{data.query.radiusKm}km · POS {data.query.countryCode}
          </div>
          {(data.providers || []).length === 0 && <div style={{ fontSize: 13, color: '#64748b' }}>{data.note || 'No active providers.'}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(data.providers || []).map(p => (
              <div key={p.provider} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{p.label} <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{p.searchEnabled ? '' : '(search off)'}</span></div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {p.error
                      ? <Badge color="red">error</Badge>
                      : <>
                          <Badge color={p.priced > 0 ? 'green' : 'amber'}>{p.hotels} found · {p.priced} priced</Badge>
                          <Badge color={p.ms > 15000 ? 'red' : 'slate'}>{(p.ms / 1000).toFixed(1)}s{p.ms > 15000 ? ' — dropped in prod' : ''}</Badge>
                        </>}
                  </div>
                </div>
                {p.error && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{p.error}</div>}
                {p.sample && p.sample.length > 0 && (
                  <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 6 }}>
                    e.g. {p.sample.map(s => `${s.name || s.id} (${s.rates} rates)`).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
